import { DOMSTRING, mobileClass } from './init';

const ACTIVE_CLASS = 'is-active';
const SCROLL_ACTIVATION_OFFSET = 120;
const OBSERVER_BOTTOM_MARGIN = '70%';

const tocCleanupHandlers = new WeakMap();

const handleMobileSelectChange = (event) => {
	const targetSelector = event.target.value;
	const targetElement = targetSelector ? document.querySelector(targetSelector) : null;

	if (!targetElement) {
		return;
	}

	targetElement.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
};

const updateActiveLink = (tocLinks, nextActiveId) => {
	if (!nextActiveId) {
		return;
	}

	tocLinks.forEach((link, anchorId) => {
		link.classList.toggle(ACTIVE_CLASS, anchorId === nextActiveId);
	});
};

export const cleanupTocInstance = (tocElement) => {
	const cleanup = tocCleanupHandlers.get(tocElement);
	if (cleanup) {
		cleanup();
		tocCleanupHandlers.delete(tocElement);
	}
};

export const addSelectEventListener = () => {
	document.querySelectorAll(`${DOMSTRING.tocMobile} select`).forEach((select) => {
		select.onchange = handleMobileSelectChange;
	});
};

export const setupScrollHighlighting = (toc) => {
	const isMobile = toc.classList.contains(mobileClass);

	if (isMobile) {
		return;
	}

	const tocLinks = new Map();
	toc.querySelectorAll('a[href^="#"]').forEach((link) => {
		const href = link.getAttribute('href');
		if (!href || href.length < 2) {
			return;
		}

		const targetId = href.slice(1);
		tocLinks.set(targetId, link);
	});

	const anchors = Array.from(tocLinks.keys())
		.map((anchorId) => document.getElementById(anchorId))
		.filter(Boolean);

	if (anchors.length === 0) {
		return;
	}

	if (typeof window.IntersectionObserver !== 'function') {
		return;
	}

	const anchorOrder = new Map(anchors.map((anchor, index) => [anchor.id, index]));
	const visibleAnchors = new Set();
	let currentActiveId = anchors[0].id;

	const getFirstVisibleAnchorId = () => {
		if (visibleAnchors.size === 0) {
			return currentActiveId;
		}

		let nextActiveId = null;
		let minIndex = Number.POSITIVE_INFINITY;

		visibleAnchors.forEach((anchorId) => {
			const index = anchorOrder.get(anchorId);
			if (index < minIndex) {
				minIndex = index;
				nextActiveId = anchorId;
			}
		});

		return nextActiveId || currentActiveId;
	};

	const syncActiveLink = (nextActiveId) => {
		if (!nextActiveId) {
			return;
		}

		currentActiveId = nextActiveId;
		updateActiveLink(tocLinks, nextActiveId);
	};

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const anchorId = entry.target.id;
				if (!anchorId) {
					return;
				}

				if (entry.isIntersecting) {
					visibleAnchors.add(anchorId);
				} else {
					visibleAnchors.delete(anchorId);
				}
			});

			syncActiveLink(getFirstVisibleAnchorId());
		},
		{
			root: null,
			rootMargin: `-${SCROLL_ACTIVATION_OFFSET}px 0px -${OBSERVER_BOTTOM_MARGIN} 0px`,
			threshold: 0,
		},
	);

	anchors.forEach((anchor) => observer.observe(anchor));

	const scheduleUpdate = () => {
		syncActiveLink(getFirstVisibleAnchorId());
	};

	const handleTocClick = (event) => {
		const link = event.target.closest('a[href^="#"]');
		if (!link) {
			return;
		}

		const targetId = link.getAttribute('href').slice(1);
		if (targetId) {
			syncActiveLink(targetId);
		}

		window.requestAnimationFrame(scheduleUpdate);
	};

	window.addEventListener('resize', scheduleUpdate);
	toc.addEventListener('click', handleTocClick);

	syncActiveLink(anchors[0].id);

	tocCleanupHandlers.set(toc, () => {
		window.removeEventListener('resize', scheduleUpdate);
		toc.removeEventListener('click', handleTocClick);
		observer.disconnect();
		visibleAnchors.clear();
	});
};
